<script lang="ts">
  import { Button } from '@athena-lib/Button';
  import { Collapse } from '@athena-lib/Collapse';
  import { TextField } from '@athena-lib/Fields';
  import { KeyboardArrowDown, KeyboardArrowUp } from '@athena-lib/Icons';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import { Paragraph } from '@athena-lib/Text';
  import clsx from 'clsx';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { PasswordMeter } from '@athena-lib/PasswordMeter';
  import ButtonLink from '@athena-lib/Button/ButtonLink/ButtonLink.svelte';
  import { Visibility, VisibilityOff } from '@athena-lib/Icons';
  import { onMount } from 'svelte';
  import {
    initTrackingFlags,
    type LoginFormHandler,
    type LoginFormTrackingEvent,
    type TrackingFlag,
  } from './types';

  const {
    onLoginSubmit,
    onResetPasswordSubmit,
    onSignUpSubmit,
    googleLoginUrl,
    onTracking,
  }: {
    onLoginSubmit: LoginFormHandler;
    onResetPasswordSubmit: LoginFormHandler;
    onSignUpSubmit: LoginFormHandler;
    googleLoginUrl: string;
    onTracking?: (message: LoginFormTrackingEvent) => void;
  } = $props();

  type ResultPromise = ReturnType<LoginFormHandler>;
  type Field = 'password1' | 'password2' | 'password3';
  type PasswordInput = 'password' | 'text';

  let loginFormElement: HTMLFormElement | undefined = undefined;
  let registerFormElement: HTMLFormElement | undefined = undefined;
  let resetFormElement: HTMLFormElement | undefined = undefined;

  let openSection = $state<null | 'login' | 'reset_pw' | 'signup'>(null);
  const trackingFlags: Record<TrackingFlag, boolean> = initTrackingFlags(false);

  let visibility: Record<Field, { type: PasswordInput }> = $state({
    password1: {
      type: 'password',
    },
    password2: {
      type: 'password',
    },
    password3: {
      type: 'password',
    },
  });
  const initialPromise = Promise.resolve({ success: true, notifications: [] });

  let loginPromise = $state<ResultPromise>(initialPromise);
  let resetPromise = $state<ResultPromise>(initialPromise);
  let signUpPromise = $state<ResultPromise>(initialPromise);

  const translations = getCommonTranslations();

  let newPassword = $state('');
  let newPasswordRepeat = $state('');

  let passwordValid = $state(false);

  const updatePasswordType = (e: Event, field: Field) => {
    e.preventDefault();
    visibility[field].type = visibility[field].type === 'password' ? 'text' : 'password';
  };

  onMount(() => {
    onHeaderClicked('login');
  });

  function onHeaderClicked(sectionName: typeof openSection) {
    openSection = openSection === sectionName ? null : sectionName;
    if (openSection !== null) {
      triggerTrackingEvent(`${openSection}_form_show`);
    }
  }

  function triggerTrackingEvent(eventAction: TrackingFlag, repeatable: boolean = false) {
    if (!onTracking) {
      return;
    }

    if (!trackingFlags[eventAction] || repeatable) {
      trackingFlags[eventAction] = true;
      onTracking({
        event_action: eventAction,
        event_group: eventAction.replace(/_[^_]*$/, ''),
        event_variant: 'login_modal',
      });
    }
  }

  function triggerErrorEvent(type: string, message: string) {
    if (!onTracking) {
      return;
    }

    onTracking({
      event_action: `${type}_form_error`,
      event_group: `${type}_form`,
      event_variant: 'login_modal',
      error_type: 'error',
      error_message: message,
    });
  }

  function maybeTriggerSignupCompleteEvent() {
    if (!registerFormElement?.checkValidity()) {
      return;
    }

    if (!passwordValid) {
      return;
    }

    if (newPassword != newPasswordRepeat) {
      return;
    }

    triggerTrackingEvent('signup_form_complete');
  }
</script>

{#snippet showNotification(result: ResultPromise, type: string)}
  {#await result then value}
    {@const error = value.success === false}
    {@const notifications = value.notifications ?? (error ? ['Something went wrong'] : [])}
    {#if error}
      {triggerErrorEvent(type, notifications[0])}
    {/if}
    {#if notifications.length > 0}
      <StackItem>
        {#each notifications as notification}
          <Paragraph>{notification}</Paragraph>
        {/each}
      </StackItem>
    {/if}
  {:catch}
    <StackItem>
      <Paragraph>Something went wrong</Paragraph>
    </StackItem>
  {/await}
{/snippet}

{#snippet collapseHeader(title: string, isOpen: boolean, sectionName: typeof openSection)}
  <button
    type="button"
    class={clsx(
      'flex w-full flex-row justify-between px-5 py-4 font-bold text-st-gray-700',
      isOpen ? 'bg-st-white' : 'bg-st-gray-200',
    )}
    onclick={() => onHeaderClicked(sectionName)}
  >
    <span>{title}</span>
    {#if isOpen}
      <KeyboardArrowUp sizeClass="w-6 h-6"></KeyboardArrowUp>
    {:else}
      <KeyboardArrowDown sizeClass="w-6 h-6"></KeyboardArrowDown>
    {/if}
  </button>
{/snippet}

{#snippet googleLoginButton()}
  <div data-sveltekit-reload={true}>
    <ButtonLink
      link={{ href: googleLoginUrl }}
      wide
      size="small"
      class="flex w-full flex-row items-center gap-0 border-2 border-solid border-[#4285f4] !bg-[#4285f4] pl-2 text-st-white hover:border-[#3367d6]"
    >
      <div class="rounded-full bg-white p-2">
        <img src="https://d6ce0no7ktiq.cloudfront.net/images/svg/google_g.svg" alt="google" />
      </div>
      <div class="flex-grow">
        {translations.loginWithGoogle}
      </div>
    </ButtonLink>
  </div>
{/snippet}

<Collapse open={openSection === 'login'}>
  {#snippet header(isOpen)}
    {@render collapseHeader(translations.login, isOpen, 'login')}
  {/snippet}
  {#snippet body()}
    <form
      bind:this={loginFormElement}
      onsubmit={(e) => {
        triggerTrackingEvent('login_form_submit', true);
        loginPromise = onLoginSubmit(e);
      }}
    >
      <Stack class="p-4">
        <StackItem>
          {@render googleLoginButton()}
        </StackItem>
        <StackItem>
          <hr />
        </StackItem>
        <StackItem>
          <TextField
            label={translations.email}
            type="email"
            name="email"
            autocomplete="off"
            oninput={() => {
              triggerTrackingEvent('login_form_start');
              if (loginFormElement?.checkValidity()) {
                triggerTrackingEvent('login_form_complete');
              }
            }}
          />
        </StackItem>
        <StackItem class="relative">
          <TextField
            label={translations.password}
            type={visibility['password1'].type}
            name="password"
            required
            autocomplete="off"
            oninput={() => {
              triggerTrackingEvent('login_form_start');
              if (loginFormElement?.checkValidity()) {
                triggerTrackingEvent('login_form_complete');
              }
            }}
          ></TextField>
          <div class="align-center absolute right-3 top-4 flex items-center justify-end">
            <button type="button" onclick={(e) => updatePasswordType(e, 'password1')}>
              {#if visibility['password1'].type === 'password'}
                <Visibility sizeClass="h-6 w-6"></Visibility>
              {:else}
                <VisibilityOff sizeClass="h-6 w-6"></VisibilityOff>
              {/if}
            </button>
          </div>
        </StackItem>
        {@render showNotification(loginPromise, 'login')}
        <StackItem>
          <Button size="small" class="w-full" type="submit">{translations.login}</Button>
        </StackItem>
      </Stack>
    </form>
  {/snippet}
</Collapse>
<Collapse open={openSection === 'signup'}>
  {#snippet header(isOpen)}
    {@render collapseHeader(translations.signup, isOpen, 'signup')}
  {/snippet}
  {#snippet body()}
    <form
      bind:this={registerFormElement}
      onsubmit={(e) => {
        triggerTrackingEvent('signup_form_submit', true);
        signUpPromise = onSignUpSubmit(e);
      }}
    >
      <Stack class="p-4">
        <StackItem>
          {@render googleLoginButton()}
        </StackItem>
        <StackItem>
          <hr />
        </StackItem>
        <StackItem>
          <TextField
            label={translations.email}
            type="email"
            name="users[credentials][email]"
            autocomplete="off"
            oninput={() => {
              triggerTrackingEvent('signup_form_start');
              maybeTriggerSignupCompleteEvent();
            }}
          />
        </StackItem>
        <StackItem class="relative">
          <TextField
            label={translations.password}
            type={visibility['password2'].type}
            name="users[credentials][password_field_group][custom_new_password]"
            bind:value={newPassword}
            required
            autocomplete="off"
            oninput={() => {
              triggerTrackingEvent('signup_form_start');
              maybeTriggerSignupCompleteEvent();
            }}
          ></TextField>
          <div class="align-center absolute right-3 top-4 flex items-center justify-end">
            <button type="button" onclick={(e) => updatePasswordType(e, 'password2')}>
              {#if visibility['password2'].type === 'password'}
                <Visibility sizeClass="h-6 w-6"></Visibility>
              {:else}
                <VisibilityOff sizeClass="h-6 w-6"></VisibilityOff>
              {/if}
            </button>
          </div>
        </StackItem>
        <StackItem class="relative">
          <TextField
            label={translations.confirmPassword}
            type={visibility['password3'].type}
            name="users[credentials][password_field_group][custom_new_password_confirm]"
            bind:value={newPasswordRepeat}
            required
            autocomplete="off"
            oninput={() => {
              triggerTrackingEvent('signup_form_start');
              maybeTriggerSignupCompleteEvent();
            }}
          ></TextField>
          <div class="align-center absolute right-3 top-4 flex items-center justify-end">
            <button type="button" onclick={(e) => updatePasswordType(e, 'password3')}>
              {#if visibility['password3'].type === 'password'}
                <Visibility sizeClass="h-6 w-6"></Visibility>
              {:else}
                <VisibilityOff sizeClass="h-6 w-6"></VisibilityOff>
              {/if}
            </button>
          </div>
        </StackItem>
        {@render showNotification(signUpPromise, 'signup')}
        <StackItem>
          <Button size="small" class="w-full" type="submit">{translations.signup}</Button>
        </StackItem>
        <StackItem>
          <PasswordMeter input={newPassword} bind:isValid={passwordValid}></PasswordMeter>
        </StackItem>
      </Stack>
    </form>
  {/snippet}
</Collapse>
<Collapse open={openSection === 'reset_pw'}>
  {#snippet header(isOpen)}
    {@render collapseHeader(translations.resetPassword, isOpen, 'reset_pw')}
  {/snippet}
  {#snippet body()}
    <form
      bind:this={resetFormElement}
      onsubmit={(e) => {
        triggerTrackingEvent('reset_pw_form_submit', true);
        resetPromise = onResetPasswordSubmit(e);
      }}
    >
      <Stack class="p-4">
        <StackItem>
          <Paragraph>{translations.resetPasswordDescription}</Paragraph>
        </StackItem>
        <StackItem>
          <TextField
            label={translations.email}
            type="email"
            name="email"
            autocomplete="off"
            oninput={() => {
              triggerTrackingEvent('reset_pw_form_start');
              if (resetFormElement?.checkValidity()) {
                triggerTrackingEvent('reset_pw_form_complete');
              }
            }}
          />
        </StackItem>
        {@render showNotification(resetPromise, 'reset_pw')}
        <StackItem>
          <Button size="small" class="w-full" type="submit">{translations.resetPassword}</Button>
        </StackItem>
      </Stack>
    </form>
  {/snippet}
</Collapse>
